<template>
    <div
        :class="[
            heightClasses,
            classes,
            'relative overflow-hidden',
            usingFallback ? fallbackBorder : '',
        ]"
    >
        <transition name="fade">
            <div
                v-if="loading || (!hadError && !imageLoaded && src != null)"
                :class="`absolute w-full h-full bg-${modelClass}-accent`"
            >
                <div :class="`bg-${modelClass} w-full h-full animate-pulse`" />
            </div>
        </transition>
        <!-- || loading is because we want the image to take up it's height if loading -->
        <img
            ref="model-image"
            v-if="(!hadError && src != null) || loading"
            :src="src"
            :alt="alt"
            :class="[
                imageClasses,
                objectFitClasses,
                {
                    'hover:brightness-90 transition hover:scale-105 translate duration-card-hover':
                        clickable,
                },
            ]"
        />
        <div
            v-else-if="!useGradient"
            :class="[
                imageClasses,
                computedClasses,
                'flex items-center justify-center min-w-full h-full',
            ]"
        >
            <component
                v-if="!hideIcon"
                :is="icon"
                :class="[
                    useAccentBg
                        ? `text-${modelClass}`
                        : `text-${modelClass}-accent`,
                    iconSize,
                ]"
            />
        </div>
        <GradiantPlaceholder
            v-else
            :class="[
                imageClasses,
                computedClasses,
                'flex items-center justify-center min-w-full h-full',
                {
                    'hover:brightness-90 transition hover:scale-105 translate duration-card-hover':
                        clickable,
                },
            ]"
        />
    </div>
</template>

<script>
import {
    PhFile,
    PhListBullets,
    PhHeadCircuit,
    PhMicrophone,
    PhPresentationChart,
    PhLink,
    PhRss,
    PhArticle,
    PhCode,
    PhBracketsCurly,
    PhVideo,
    PhImage,
    PhImageSquare,
    PhUser,
    PhFilePdf,
    PhMicrosoftExcelLogo,
    PhMicrosoftWordLogo,
    PhMicrosoftPowerpointLogo,
    PhFileImage,
} from '@phosphor-icons/vue'

import { checkIfImageLoaded } from '@/Tools/utilities.js'
import GradiantPlaceholder from '@/Components/ModelComponents/GradiantPlaceholder.vue'
export default {
    name: 'ModelImage',

    components: {
        GradiantPlaceholder,
    },

    props: {
        src: {
            type: [String, null],
            default: null,
        },
        modelClass: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        alt: {
            type: String,
            default: null,
        },
        imageClasses: {
            type: [String, Array],
            default: null,
        },
        clickable: {
            type: Boolean,
            default: false,
        },
        useAccentBg: {
            type: Boolean,
            default: false,
        },
        heightClasses: {
            type: String,
            default: 'h-full',
        },
        classes: {
            type: String,
            default: '',
        },
        fallbackBorder: {
            type: String,
            default: '',
        },
        objectFitClasses: {
            type: [String, Array],
            default: 'object-cover object-center',
        },
        hideIcon: {
            type: Boolean,
            default: false,
        },
        iconSize: {
            type: String,
            default: 'size-8',
        },
        typeId: {
            type: [String, null],
            default: null,
        },
        fileUrl: {
            type: [String, null],
            default: null,
        },
        useGradient: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        hadError: false,
        imageLoaded: false,
    }),

    mounted() {
        if (!this.loading) {
            this.seeIfImageIsLoaded()
        }
    },

    watch: {
        loading() {
            if (this.loading == false && this.imageLoaded == false) {
                this.seeIfImageIsLoaded()
            }
        },
    },

    computed: {
        icon() {
            if (this.modelClass == 'user') {
                return PhUser
            } else if (this.modelClass == 'organization') {
                return PhImageSquare
            } else if (this.modelClass == 'resource') {
                switch (this.typeId) {
                    case 'file':
                        switch (this.fileType) {
                            case 'pdf':
                                return PhFilePdf
                            case 'xls':
                            case 'xlsx':
                                return PhMicrosoftExcelLogo
                            case 'doc':
                            case 'docx':
                                return PhMicrosoftWordLogo
                            case 'ppt':
                            case 'pptx':
                                return PhMicrosoftPowerpointLogo
                            case 'jpeg':
                            case 'jpg':
                            case 'png':
                            case 'gif':
                                return PhFileImage
                            default:
                                return PhFile
                        }
                    case 'dynamiclist':
                    case 'list':
                        return PhListBullets
                    case 'dataset':
                    case 'model':
                    case 'prompt':
                        return PhHeadCircuit
                    case 'podcast':
                        return PhMicrophone
                    case 'presentation':
                        return PhPresentationChart
                    case 'resourcelink':
                    case 'web':
                    case 'social':
                        return PhLink
                    case 'feed':
                        return PhRss
                    case 'content':
                    case 'post':
                    case 'text':
                        return PhArticle
                    case 'html':
                        return PhCode
                    case 'code':
                        return PhBracketsCurly
                    case 'video':
                        return PhVideo
                    case 'image':
                    default:
                        return PhImage
                }
            } else {
                return PhImage
            }
        },

        fileType() {
            if (this.fileUrl != null) {
                return this.fileUrl.split('.').pop()
            }
            return null
        },

        usingFallback() {
            return this.hadError || this.src == null
        },

        computedClasses() {
            let classes = []
            if (this.clickable) {
                classes.push(
                    `hover:scale-110 translate transition duration-card-hover`
                )
                if (this.useAccentBg) {
                    classes.push(`hover:bg-${this.modelClass}-accent-hover`)
                } else {
                    classes.push(`hover:bg-${this.modelClass}-hover`)
                }
            }
            if (this.useAccentBg) {
                classes.push(`bg-${this.modelClass}-accent`)
            } else {
                classes.push(`bg-${this.modelClass}`)
            }

            return classes.join(' ')
        },
    },

    methods: {
        seeIfImageIsLoaded() {
            // Check if the image is loaded
            // 100 times with 100ms between each check equals 10 seconds
            checkIfImageLoaded(100, 100, () => this.$refs['model-image']).then(
                (hadError) => {
                    this.imageLoaded = true
                    this.hadError = hadError
                }
            )
        },
    },
}
</script>
